/**
 * CopilotFreeChat — the free assistant every logged-in user gets. Asks
 * POST /copilot/ask (no tools, no data access) and keeps one session so
 * follow-up questions make sense.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, Send, Loader2, User as UserIcon } from 'lucide-react';

import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const SUGGESTIONS = [
  'Where do I add a new member?',
  'Draft a short announcement for Sunday service',
  'What does "lapsed" mean on the affiliates page?',
  'How do I record an offering?',
];

export const CopilotFreeChat = () => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const ask = useMutation({
    mutationFn: (prompt) => api.post('/copilot/ask', { prompt, session_id: sessionId })
      .then((r) => r.data),
    onSuccess: (d) => {
      if (d?.session_id) setSessionId(d.session_id);
      setMessages((m) => [...m, {
        role: 'assistant',
        text: d?.ok ? d.narration : (d?.error || 'Copilot could not answer just now'),
        failed: !d?.ok,
      }]);
    },
    onError: (e) => setMessages((m) => [...m, {
      role: 'assistant', failed: true,
      text: e.response?.data?.detail || 'Copilot could not answer just now',
    }]),
  });

  const send = (text) => {
    const prompt = (text ?? draft).trim();
    if (!prompt || ask.isPending) return;
    setMessages((m) => [...m, { role: 'user', text: prompt }]);
    setDraft('');
    ask.mutate(prompt);
  };

  return (
    <div className="flex flex-col h-full" data-testid="copilot-free-chat">
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Ask me anything about the platform — where to click, what a term means, or ask me
              to draft some wording for you.
            </p>
            <div className="space-y-1.5">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)}
                        data-testid={`copilot-free-suggestion-${i}`}
                        className="w-full text-left text-xs px-3 py-2 rounded-md border border-slate-200
                                   hover:border-violet-300 hover:bg-violet-50 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}
               data-testid={`copilot-free-msg-${i}`}>
            {m.role === 'assistant' && (
              <Sparkles className={`h-4 w-4 mt-1 shrink-0 ${m.failed ? 'text-rose-500' : 'text-violet-600'}`} />
            )}
            <div className={`text-sm rounded-lg px-3 py-2 max-w-[85%] whitespace-pre-wrap ${
              m.role === 'user' ? 'bg-slate-900 text-white'
                : m.failed ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-800'}`}>
              {m.text}
            </div>
            {m.role === 'user' && <UserIcon className="h-4 w-4 mt-1 shrink-0 text-slate-400" />}
          </div>
        ))}

        {ask.isPending && (
          <p className="text-xs text-slate-400 flex items-center gap-2" data-testid="copilot-free-thinking">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
          </p>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t p-3 flex items-center gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)}
               placeholder="Ask Copilot…" data-testid="copilot-free-input" />
        <Button type="submit" size="icon" disabled={ask.isPending || !draft.trim()}
                data-testid="copilot-free-send">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
};

export default CopilotFreeChat;
